/**
 * Arthaus Discovery Grid — shared, animated gridwall renderer
 * Phase: 9 (discovery)
 *
 * One card + render used by every discovery surface (facets, concept chips, load-more) so
 * the gridwall transitions are consistent and delightful: as results change, tiles settle in
 * with a staggered fade + rise + subtle scale. Every tile carries the "More like this"
 * affordance, so the graph-walk (discovery-walk.js) can be launched from any result.
 *
 * Requires assets/arthaus-card.js (the shared card markup) loaded alongside.
 *
 * window.ArthausGrid = { card(product, opts), render(grid, products, opts) }
 *   opts.mlt (default true) — include the More-like-this launcher
 *   opts.append           — append + animate only the new tiles (load-more)
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EASE = 'cubic-bezier(.22,.68,.28,1)';
  var SKELETON_DELAY = 220; // ms before showing skeletons (fast responses never flash them)

  // Shimmer keyframes for the loading skeletons (injected once).
  (function injectStyle() {
    if (document.getElementById('arthaus-grid-style')) return;
    var s = document.createElement('style');
    s.id = 'arthaus-grid-style';
    s.textContent =
      '@keyframes gwShimmer{100%{transform:translateX(100%)}}' +
      '.gw-shimmer{position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);transform:translateX(-100%);animation:gwShimmer 1.35s infinite}' +
      // Skeleton fills defined here (not Tailwind) — the theme's Tailwind is AOT-compiled,
      // so arbitrary bg-*/[opacity] classes wouldn\'t exist in the CSS.
      '.gw-skel-img{background:rgba(45,45,45,.09);position:relative;overflow:hidden}' +
      '.gw-skel-bar{background:rgba(45,45,45,.10);border-radius:4px}' +
      '.gw-skel-bar.sm{background:rgba(45,45,45,.06)}' +
      '[data-product-grid]{transition:opacity .2s ease}';
    document.head.appendChild(s);
  })();

  // Card markup lives in ONE place — assets/arthaus-card.js (mirrors snippets/product-card.liquid).
  // "More like this" is on by default for every discovery tile.
  function card(p, opts) {
    opts = opts || {};
    if (!window.ArthausCard) return '';
    return window.ArthausCard.render(p, { mlt: opts.mlt !== false, hideArtist: opts.hideArtist });
  }

  // Staggered settle-in: fade + rise + subtle scale, capped stagger so large pages stay snappy.
  function animateIn(grid, fromIndex) {
    var kids = grid.children;
    var shown = 0;
    for (var i = fromIndex || 0; i < kids.length; i++) {
      var el = kids[i];
      el.style.opacity = '0';
      el.style.transform = 'translateY(16px) scale(.985)';
      el.style.willChange = 'opacity, transform';
      (function (node, order) {
        var delay = Math.min(order * 32, 420);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            node.style.transition = 'opacity .55s ' + EASE + ' ' + delay + 'ms, transform .6s ' + EASE + ' ' + delay + 'ms';
            node.style.opacity = '1';
            node.style.transform = 'none';
          });
        });
        setTimeout(function () { node.style.willChange = ''; node.style.transition = ''; node.style.transform = ''; }, 700 + delay);
      })(el, shown++);
    }
  }

  function skeletonCard() {
    return '<div class="gw-skel">' +
      '<div class="gw-skel-img aspect-[3/4] rounded-lg mb-4 lg:mb-5"><div class="gw-shimmer"></div></div>' +
      '<div class="gw-skel-bar h-4 w-2/3 mb-2"></div>' +
      '<div class="gw-skel-bar sm h-3 w-1/3"></div>' +
    '</div>';
  }

  // Intelligent load state: dim immediately; only swap to shimmer skeletons if the
  // request is actually slow (cold Cloud Run / LLM), so fast filters never flash.
  function beginLoad(grid, count) {
    if (!grid) return;
    clearTimeout(grid._gwTimer);
    grid.style.opacity = '0.5';
    grid._gwTimer = setTimeout(function () {
      var n = Math.max(8, Math.min(count || grid.children.length || 12, 12));
      var sk = '';
      for (var i = 0; i < n; i++) sk += skeletonCard();
      grid.style.opacity = '1';
      grid.setAttribute('data-gw-loading', '');
      grid.innerHTML = sk;
    }, reduce ? 0 : SKELETON_DELAY);
  }

  function endLoad(grid) {
    if (!grid) return;
    clearTimeout(grid._gwTimer);
    grid.removeAttribute('data-gw-loading');
    grid.style.opacity = '';
  }

  function render(grid, products, opts) {
    if (!grid) return;
    opts = opts || {};
    var html = (products || []).map(function (p) { return card(p, opts); }).filter(Boolean).join('');
    if (opts.append) {
      endLoad(grid);
      var start = grid.children.length;
      grid.insertAdjacentHTML('beforeend', html);
      if (!reduce) animateIn(grid, start);
      return;
    }
    endLoad(grid);
    grid.innerHTML = html || '<p class="col-span-full font-body text-sm text-mute py-16 text-center">No works match these filters.</p>';
    if (!reduce) animateIn(grid, 0);
  }

  // Full product-handle list for a collection, via the storefront products.json (paged).
  // Regular ("room-spill") collections have no graph concept_key, so discovery reasons over
  // the collection's OWN products; Liquid caps at 50, this covers the whole set (to a bound).
  // Cached in sessionStorage so the facets + chips controllers share one fetch.
  var HANDLE_CAP_PAGES = 8; // 8 × 250 = 2000; beyond that we filter over a (logged) sample.
  function collectionHandles(handle) {
    if (!handle) return Promise.resolve([]);
    var key = 'gfh:' + handle;
    try { var s = sessionStorage.getItem(key); if (s) return Promise.resolve(JSON.parse(s)); } catch (e) { /* private mode */ }
    var all = [];
    function page(p) {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + p, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) {
          var ps = d.products || [];
          for (var i = 0; i < ps.length; i++) if (ps[i].handle) all.push(ps[i].handle);
          if (ps.length === 250 && p < HANDLE_CAP_PAGES) return page(p + 1);
          if (ps.length === 250 && p >= HANDLE_CAP_PAGES) console.info('[ArthausGrid] handle pool capped at', all.length, 'for', handle);
          return all;
        });
    }
    return page(1).then(function (list) {
      try { sessionStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* quota */ }
      return list;
    }).catch(function () { return []; });
  }

  window.ArthausGrid = { card: card, render: render, animateIn: animateIn, beginLoad: beginLoad, endLoad: endLoad, collectionHandles: collectionHandles };
})();
